// IND-015: lógica de presentación del Panel de planta (sin cambiar cálculos):
// comparación con el año anterior, avisos agrupados por tipo, colores de carga,
// fechas de las OF en la tarjeta y estadísticas de cada línea.
import type { PlantDataNotice, PlantDataNoticeLevel } from '../services/industrialCostService';
import type { PlantDashboardOrder } from '../services/plantDashboardService';
import type { PanelProductionRow } from '../types/industrialCost';

// ---------------------------------------------------------------------------
// Comparación con el mismo periodo del año anterior
// ---------------------------------------------------------------------------

export type YearComparison = {
  direction: 'up' | 'down' | 'flat';
  /** Variación en %, o null si no hay periodo comparable. */
  pct: number | null;
};

/** Gris («flat») si falta el dato, vale 0 o la variación redondea a 0,0 %. */
export function compareWithPreviousYear(
  current: number | null | undefined,
  previous: number | null | undefined,
): YearComparison {
  if (current == null || previous == null || !Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) {
    return { direction: 'flat', pct: null };
  }
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.round(pct * 10) === 0) return { direction: 'flat', pct: 0 };
  return { direction: pct > 0 ? 'up' : 'down', pct };
}

// ---------------------------------------------------------------------------
// Avisos de datos agrupados por tipo
// ---------------------------------------------------------------------------

/** Nombre corto de cada tipo de aviso (propuesto para que Pablo lo valide). */
export const NOTICE_TYPE_NAMES: Record<string, string> = {
  producto_sin_escandallo: 'Producto sin escandallo',
  producto_inactivo: 'Producto desactivado',
  producto_no_reconocido: 'Producto no reconocido',
  material_no_existe: 'Material del escandallo que no existe',
  consumo_no_valido: 'Consumo no válido en el escandallo',
  regla_repetida: 'Fila repetida en el escandallo',
  regla_no_guardada: 'Regla del escandallo no guardada',
  material_sin_recuento: 'Material sin recuento',
  stock_negativo: 'Stock calculado negativo',
  produccion_sin_cliente: 'Producción sin cliente',
  of_sin_cliente: 'OF sin cliente',
  cliente_sin_personalizacion: 'Cliente sin personalización',
  // IND-020
  cliente_varias_personalizaciones: 'Cliente con varias personalizaciones',
  material_sin_uso: 'Material sin uso',
  fuera_del_ultimo_recuento: 'Fuera del último recuento (se toma 0)',
  caja_sin_caja_real: 'Caja sin caja real',
  caja_real_no_existe: 'Caja real que no existe',
  regla_a_caja_real: 'Escandallo que apunta a una caja real',
  caja_no_utilizable: 'Caja del escandallo no utilizable',
  personalizacion_sin_cajas: 'Personalización sin cajas reales',
  compra_articulo_no_material: 'Compra de un artículo que no es material',
  material_sin_tipo: 'Material sin tipo',
  // IND-029
  material_sin_unidad: 'Material sin unidad',
  // IND-032
  materia_prima_sin_destino: 'Materia prima sin destino',
  materia_prima_sin_porcentaje_destino: 'Materia prima sin % DESTINO 1',
  materia_prima_sin_precio: 'Materia prima sin precio',
  sin_lineas_configuradas: 'Líneas sin configurar',
  of_sin_maquina: 'OF sin máquina',
  of_maquina_no_reconocida: 'Máquina no reconocida',
  of_sin_horas: 'OF sin horas pendientes',
  of_sin_fecha_expedicion: 'OF sin fecha de expedición',
  of_corregida_por_partes: 'OF corregida con los partes',
  reglas_por_kg: 'Reglas por kg sin usar',
  compra_vencida_o_sin_fecha: 'Compra vencida o sin fecha',
  // IND-022: un aviso por día.
  dia_laborable_sin_produccion: 'Día laborable sin producción',
};

export function noticeTypeName(notice: PlantDataNotice): string {
  if (notice.code.startsWith('ficheros_dias_distintos')) return 'Ficheros de días distintos';
  // Descartes de carga: su etiqueta ya es el tipo («Filas sin fecha», …).
  if (notice.code.startsWith('carga_')) return notice.subject?.label || notice.title;
  return NOTICE_TYPE_NAMES[notice.code] ?? notice.subject?.label ?? notice.title;
}

export type NoticeGroup = {
  key: string;
  name: string;
  level: PlantDataNoticeLevel;
  fileKey: string;
  fileName: string | null;
  /** Cosas a corregir (p. ej. 37 productos sin escandallo). */
  count: number;
  /** Documentos afectados sumados (orden dentro de cada nivel). */
  documents: number;
  notices: PlantDataNotice[];
};

const LEVEL_ORDER: Record<PlantDataNoticeLevel, number> = { blocks: 0, incomplete: 1, info: 2 };

/** Un grupo por tipo + fichero; por nivel y, dentro, de más a menos
 * documentos. Sin grupos con 0. */
export function groupNoticesByType(notices: PlantDataNotice[]): NoticeGroup[] {
  const groups = new Map<string, NoticeGroup>();
  for (const notice of notices) {
    const key = `${notice.code}|${notice.file_key}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        name: noticeTypeName(notice),
        level: notice.level,
        fileKey: notice.file_key,
        fileName: notice.file_name,
        count: 0,
        documents: 0,
        notices: [],
      };
      groups.set(key, group);
    }
    // Un aviso «por tipo» agrupa ya todos sus documentos: cada uno es una
    // cosa a corregir. Un aviso por producto o material es una sola.
    group.count += notice.subject?.kind === 'tipo' ? Math.max(notice.affected_count, 1) : 1;
    group.documents += notice.affected_count;
    if (LEVEL_ORDER[notice.level] < LEVEL_ORDER[group.level]) group.level = notice.level;
    group.notices.push(notice);
  }
  return [...groups.values()]
    .filter((group) => group.count > 0)
    .sort(
      (a, b) =>
        LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level] ||
        b.documents - a.documents ||
        b.count - a.count ||
        a.name.localeCompare(b.name, 'es'),
    );
}

// ---------------------------------------------------------------------------
// Tarjetas de carga por línea
// ---------------------------------------------------------------------------

export const WORKLOAD_GREEN = '#86efac';
export const WORKLOAD_ORANGE = '#fdba74';
export const WORKLOAD_RED = '#fca5a5';

/** Verde ≤ 75 % del plazo, naranja > 75 % y ≤ 100 %, rojo por encima. */
export function workloadColor(ratio: number | null, muted: string): string {
  if (ratio === null || !Number.isFinite(ratio)) return muted;
  if (ratio <= 0.75) return WORKLOAD_GREEN;
  if (ratio <= 1) return WORKLOAD_ORANGE;
  return WORKLOAD_RED;
}

export type OrderDateTone = 'late' | 'normal' | 'theoretical' | 'none';

export function orderDisplayDate(order: PlantDashboardOrder, todayIso: string): { day: string | null; tone: OrderDateTone } {
  const dispatch = (order.dispatch_date ?? '').slice(0, 10);
  if (dispatch) return { day: dispatch, tone: dispatch < todayIso ? 'late' : 'normal' };
  const theoretical = (order.theoretical_dispatch_date ?? '').slice(0, 10);
  if (theoretical) return { day: theoretical, tone: 'theoretical' };
  return { day: null, tone: 'none' };
}

/** Próximas expediciones: por fecha (expedición o teórica); sin fecha al final. */
export function sortOrdersForCard(orders: PlantDashboardOrder[], todayIso: string): PlantDashboardOrder[] {
  return [...orders].sort((a, b) => {
    const da = orderDisplayDate(a, todayIso).day;
    const db = orderDisplayDate(b, todayIso).day;
    if (da === db) return String(a.manufacturing_order_number ?? '').localeCompare(String(b.manufacturing_order_number ?? ''));
    if (da === null) return 1;
    if (db === null) return -1;
    return da.localeCompare(db);
  });
}

export type OrderSortKey = 'manufacturing_order_number' | 'product_ref' | 'pending_m' | 'dispatch_date' | 'customer_number';

/** Orden de la ventana «Ver las N OF»; los vacíos siempre al final. */
export function sortOrdersBy(
  orders: PlantDashboardOrder[],
  key: OrderSortKey,
  direction: 'asc' | 'desc',
): PlantDashboardOrder[] {
  const sign = direction === 'asc' ? 1 : -1;
  return [...orders].sort((a, b) => {
    const va = a[key];
    const vb = b[key];
    const emptyA = va === null || va === undefined || va === '';
    const emptyB = vb === null || vb === undefined || vb === '';
    if (emptyA && emptyB) return 0;
    if (emptyA) return 1;
    if (emptyB) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sign;
    return String(va).localeCompare(String(vb), 'es', { numeric: true }) * sign;
  });
}

// ---------------------------------------------------------------------------
// Estadísticas de una línea
// ---------------------------------------------------------------------------

export type LineStats = {
  currentMeters: number | null;
  previousMeters: number | null;
  recentWeeklyMeters: number | null;
  previousRecentWeeklyMeters: number | null;
  /** Días distintos con metros > 0; null si la línea solo tiene datos mensuales o ninguno. */
  productionDays: number | null;
};

function sumMeters(rows: PanelProductionRow[]): number | null {
  const valid = rows.filter((row) => row.units_produced !== null);
  if (!valid.length) return null;
  return valid.reduce((total, row) => total + Number(row.units_produced ?? 0), 0);
}

function shiftDays(iso: string, days: number): string {
  const value = new Date(`${iso}T12:00:00`);
  value.setDate(value.getDate() + days);
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, '0'),
    String(value.getDate()).padStart(2, '0'),
  ].join('-');
}

/** Año en curso frente al mismo periodo del anterior. Las dos ventanas del
 * ritmo (8 semanas) terminan el último día con producción cargada y en la
 * misma fecha del año anterior. */
export function buildLineStats(rows: PanelProductionRow[], currentYear: number, lineId: string | null): LineStats {
  const empty: LineStats = {
    currentMeters: null,
    previousMeters: null,
    recentWeeklyMeters: null,
    previousRecentWeeklyMeters: null,
    productionDays: null,
  };
  if (!lineId) return empty;

  const isDaily = (row: PanelProductionRow, year: number) =>
    row.source_granularity === 'day' && Boolean(row.production_date) && row.production_date!.startsWith(`${year}-`);

  const currentDaily = rows.filter((row) => isDaily(row, currentYear));
  if (currentDaily.length === 0) return empty;

  const dates = currentDaily.map((row) => row.production_date!.slice(0, 10)).sort();
  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];
  const previousYear = currentYear - 1;

  const currentLine = currentDaily.filter((row) => row.production_line_config_id === lineId);
  const previousLine = rows.filter(
    (row) =>
      isDaily(row, previousYear) &&
      row.production_line_config_id === lineId &&
      row.production_date!.slice(5, 10) >= firstDate.slice(5) &&
      row.production_date!.slice(5, 10) <= lastDate.slice(5),
  );

  // La ventana puede cruzar el cambio de año: se leen las filas diarias de la
  // línea de cualquier año dentro de cada ventana.
  const lineDaily = rows.filter(
    (row) => row.source_granularity === 'day' && Boolean(row.production_date) && row.production_line_config_id === lineId,
  );
  const recentStart = shiftDays(lastDate, -55);
  const previousRecentStart = `${Number(recentStart.slice(0, 4)) - 1}-${recentStart.slice(5)}`;
  const previousRecentEnd = `${previousYear}-${lastDate.slice(5)}`;
  const recent = sumMeters(
    lineDaily.filter((row) => row.production_date!.slice(0, 10) >= recentStart && row.production_date!.slice(0, 10) <= lastDate),
  );
  const previousRecent = sumMeters(
    lineDaily.filter(
      (row) => row.production_date!.slice(0, 10) >= previousRecentStart && row.production_date!.slice(0, 10) <= previousRecentEnd,
    ),
  );

  const productionDays = currentLine.length
    ? new Set(
        currentLine.filter((row) => Number(row.units_produced ?? 0) > 0).map((row) => row.production_date!.slice(0, 10)),
      ).size
    : null;

  return {
    currentMeters: sumMeters(currentLine),
    previousMeters: sumMeters(previousLine),
    recentWeeklyMeters: recent === null ? null : recent / 8,
    previousRecentWeeklyMeters: previousRecent === null ? null : previousRecent / 8,
    productionDays,
  };
}

// ---------------------------------------------------------------------------
// Unidad válida («nan» o vacío = sin unidad)
// ---------------------------------------------------------------------------

// IND-030: «ud», «uds», «unidad» y «unidades» son la misma unidad: se
// muestran «unidades».
const UNIT_SYNONYMS = new Set(['ud', 'uds', 'u', 'un', 'und', 'unidad', 'unidades']);

export function validUnit(unit: string | null | undefined): string | null {
  const text = String(unit ?? '').trim();
  if (!text || ['nan', 'none', 'null', 'undefined'].includes(text.toLowerCase())) return null;
  return UNIT_SYNONYMS.has(text.toLowerCase()) ? 'unidades' : text;
}

export function validName(name: string | null | undefined): string | null {
  // Solo descarta «nan» y vacíos (los sinónimos de unidad son cosa de validUnit).
  const text = String(name ?? '').trim();
  if (!text || ['nan', 'none', 'null', 'undefined'].includes(text.toLowerCase())) return null;
  return text;
}

// IND-029: el nombre solo si es real y distinto del código (si no, el código
// una vez). Los nombres antiguos se conservan para reconocer el material.
export function realMaterialName(
  name: string | null | undefined,
  code: string | null | undefined,
): string | null {
  const text = validName(name);
  if (!text || text === String(code ?? '').trim()) return null;
  return text;
}
